import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../../lib/utils';
import { 
  Store, Users, Plus, LogOut, Eye, Pencil, Trash2, 
  Key, ChefHat, ShoppingCart, DollarSign, Package,
  BarChart3, Settings, Bell, CheckCircle, Clock, Mail, Copy,
  Database, Download, HardDrive, FileJson, FileText, Ban, Check, X,
  Phone, MapPin, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const [establishments, setEstablishments] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('establishments');
  
  // Dialogs
  const [showNewEstablishment, setShowNewEstablishment] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [showEstablishmentStats, setShowEstablishmentStats] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showNewPasswordModal, setShowNewPasswordModal] = useState(null); // { user, password }
  
  // Forms
  const [newEstablishment, setNewEstablishment] = useState({ name: '', slug: '', description: '', phone: '', address: '', whatsapp: '' });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', establishment_id: '' });
  const [establishmentStats, setEstablishmentStats] = useState(null);
  
  // Backup
  const [backupStats, setBackupStats] = useState(null);
  const [isBackingUp, setIsBackingUp] = useState(false);

  // Interest Requests
  const [interestRequests, setInterestRequests] = useState([]);
  const [pendingInterestCount, setPendingInterestCount] = useState(0);
  const [showApproveDialog, setShowApproveDialog] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(null);
  const [approveSlug, setApproveSlug] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [approvalResult, setApprovalResult] = useState(null);
  
  // Block establishment
  const [showBlockDialog, setShowBlockDialog] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  const fetchEstablishments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/establishments`);
      setEstablishments(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifResponse, countResponse] = await Promise.all([
        axios.get(`${API}/admin/notifications`),
        axios.get(`${API}/admin/notifications/pending-count`)
      ]);
      setNotifications(notifResponse.data);
      setPendingCount(countResponse.data.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchBackupStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/stats`);
      setBackupStats(response.data);
    } catch (error) {
      console.error('Error fetching backup stats:', error);
    }
  }, []);

  const fetchInterestRequests = useCallback(async () => {
    try {
      const [requestsResponse, countResponse] = await Promise.all([
        axios.get(`${API}/admin/interest-requests`),
        axios.get(`${API}/admin/interest-requests/pending-count`)
      ]);
      setInterestRequests(requestsResponse.data);
      setPendingInterestCount(countResponse.data.count);
    } catch (error) {
      console.error('Error fetching interest requests:', error);
    }
  }, []);

  const handleApproveInterest = async () => {
    if (!showApproveDialog || !approveSlug.trim()) {
      toast.error('Informe o slug da loja');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/interest-requests/${showApproveDialog.id}/approve`, {
        slug: approveSlug.toLowerCase().replace(/\s/g, '-')
      });
      
      setApprovalResult(response.data);
      toast.success('Estabelecimento criado com sucesso!');
      fetchInterestRequests();
      fetchEstablishments();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar solicitação');
    }
  };

  const handleRejectInterest = async () => {
    if (!showRejectDialog || !rejectReason.trim()) {
      toast.error('Informe o motivo da rejeição');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/interest-requests/${showRejectDialog.id}/reject`, {
        reason: rejectReason
      });
      toast.success('Solicitação rejeitada');
      setShowRejectDialog(null);
      setRejectReason('');
      fetchInterestRequests();
    } catch (error) {
      toast.error('Erro ao rejeitar solicitação');
    }
  };

  const handleBlockEstablishment = async (blocked) => {
    if (!showBlockDialog) return;
    
    try {
      await axios.put(`${API}/admin/establishments/${showBlockDialog.id}/block`, {
        blocked,
        reason: blocked ? blockReason : null
      });
      toast.success(`Estabelecimento ${blocked ? 'bloqueado' : 'desbloqueado'}!`);
      setShowBlockDialog(null);
      setBlockReason('');
      fetchEstablishments();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const getStoreTypeLabel = (type, other) => {
    const types = {
      acai: 'Açaí e Sorvetes',
      lanches: 'Lanches e Hambúrgueres',
      pizzaria: 'Pizzaria',
      restaurante: 'Restaurante',
      supermercado: 'Supermercado',
      farmacia: 'Farmácia',
      petshop: 'Pet Shop',
      outros: other || 'Outros'
    };
    return types[type] || type;
  };

  const handleDownloadBackup = async () => {
    setIsBackingUp(true);
    try {
      const response = await axios.get(`${API}/admin/backup`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup_konectdelivery_${new Date().toISOString().slice(0,10)}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup baixado com sucesso!');
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Erro ao criar backup');
    } finally {
      setIsBackingUp(false);
    }
  };

  useEffect(() => {
    fetchEstablishments();
    fetchUsers();
    fetchNotifications();
    fetchBackupStats();
    fetchInterestRequests();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchInterestRequests();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchEstablishments, fetchUsers, fetchNotifications, fetchBackupStats, fetchInterestRequests]);

  const handleResetPasswordFromNotification = async (notificationId, userId, userEmail, userName) => {
    try {
      // 1. Reset the password
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`);
      const tempPassword = response.data.temporary_password;
      
      // 2. Resolve the notification
      await axios.put(`${API}/admin/notifications/${notificationId}/resolve`);
      
      // 3. Show the new password in a modal
      setShowNewPasswordModal({
        userName,
        userEmail,
        password: tempPassword
      });
      
      fetchNotifications();
    } catch (error) {
      toast.error('Erro ao resetar senha');
    }
  };

  const handleCreateEstablishment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/establishments`, newEstablishment);
      toast.success('Estabelecimento criado!');
      setShowNewEstablishment(false);
      setNewEstablishment({ name: '', slug: '', description: '', phone: '', address: '', whatsapp: '' });
      fetchEstablishments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar estabelecimento');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newUser,
        role: 'establishment_admin',
        establishment_id: newUser.establishment_id || null
      };
      await axios.post(`${API}/admin/users`, payload);
      toast.success('Usuário criado!');
      setShowNewUser(false);
      setNewUser({ name: '', email: '', password: '', establishment_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        name: editingUser.name,
        establishment_id: editingUser.establishment_id || null
      });
      toast.success('Usuário atualizado!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Deseja excluir este usuário?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const handleResetPassword = async () => {
    if (!showResetPassword) return;
    try {
      const response = await axios.post(`${API}/admin/users/${showResetPassword.id}/reset-password`);
      setShowResetPassword(null);
      setShowNewPasswordModal({
        userName: showResetPassword.name,
        userEmail: showResetPassword.email,
        password: response.data.temporary_password
      });
    } catch (error) {
      toast.error('Erro ao resetar senha');
    }
  };

  const handleCopyPassword = () => {
    if (showNewPasswordModal?.password) {
      navigator.clipboard.writeText(showNewPasswordModal.password);
      toast.success('Senha copiada para a área de transferência!');
    }
  };

  const handleViewStats = async (establishment) => {
    try {
      const response = await axios.get(`${API}/admin/establishments/${establishment.id}/stats`);
      setEstablishmentStats(response.data);
      setShowEstablishmentStats(establishment);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    }
  };

  const handleDeleteEstablishment = async (establishmentId) => {
    if (!window.confirm('Deseja excluir este estabelecimento? Todos os dados serão perdidos.')) return;
    try {
      await axios.delete(`${API}/establishments/${establishmentId}`);
      toast.success('Estabelecimento excluído!');
      fetchEstablishments();
    } catch (error) {
      toast.error('Erro ao excluir estabelecimento');
    }
  };

  const getEstablishmentName = (id) => {
    const est = establishments.find(e => e.id === id);
    return est?.name || '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Delivery OS</h1>
              <p className="text-slate-400 text-sm">Painel Super Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-300 text-sm">{user?.name}</span>
            <Button variant="ghost" onClick={logout} className="text-slate-300 hover:text-white" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'establishments' 
                  ? 'border-red-500 text-white' 
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('establishments')}
              data-testid="tab-establishments"
            >
              <Store className="w-4 h-4 inline mr-2" />
              Estabelecimentos ({establishments.length})
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users' 
                  ? 'border-red-500 text-white' 
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('users')}
              data-testid="tab-users"
            >
              <Users className="w-4 h-4 inline mr-2" />
              Usuários ({users.length})
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
                activeTab === 'notifications' 
                  ? 'border-red-500 text-white' 
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('notifications')}
              data-testid="tab-notifications"
            >
              <Bell className="w-4 h-4 inline mr-2" />
              Notificações
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'backup' 
                  ? 'border-green-500 text-white' 
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('backup')}
              data-testid="tab-backup"
            >
              <Database className="w-4 h-4 inline mr-2" />
              Backup
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
                activeTab === 'interest' 
                  ? 'border-orange-500 text-white' 
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('interest')}
              data-testid="tab-interest"
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Solicitações
              {pendingInterestCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {pendingInterestCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'establishments' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Estabelecimentos Cadastrados</h2>
              <Button onClick={() => setShowNewEstablishment(true)} className="bg-red-500 hover:bg-red-600" data-testid="add-establishment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Estabelecimento
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {establishments.map(est => (
                <Card key={est.id} className={`bg-slate-800 border-slate-700 ${est.is_blocked ? 'border-l-4 border-l-red-500' : ''}`} data-testid={`establishment-card-${est.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-white">{est.name}</h3>
                        <p className="text-sm text-slate-400">/loja/{est.slug}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {est.is_blocked ? (
                          <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 flex items-center gap-1">
                            <Ban className="w-3 h-3" />
                            Bloqueado
                          </span>
                        ) : (
                          <span className={`px-2 py-1 text-xs rounded ${est.is_open ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {est.is_open ? 'Aberto' : 'Fechado'}
                          </span>
                        )}
                      </div>
                    </div>
                    {est.is_blocked && est.blocked_reason && (
                      <div className="bg-red-500/10 border border-red-500/30 p-2 rounded text-xs text-red-300 mb-3">
                        <strong>Motivo:</strong> {est.blocked_reason}
                      </div>
                    )}
                    {est.description && (
                      <p className="text-sm text-slate-400 mb-3 line-clamp-2">{est.description}</p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => handleViewStats(est)} className="flex-1" data-testid={`view-stats-${est.id}`}>
                        <BarChart3 className="w-4 h-4 mr-1" />
                        Stats
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => setShowBlockDialog(est)} 
                        className={est.is_blocked ? 'text-green-400 hover:text-green-300' : 'text-amber-400 hover:text-amber-300'}
                        data-testid={`block-${est.id}`}
                      >
                        {est.is_blocked ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteEstablishment(est.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Usuários do Sistema</h2>
              <Button onClick={() => setShowNewUser(true)} className="bg-red-500 hover:bg-red-600" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Usuário
              </Button>
            </div>
            
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-4 text-slate-400 font-medium">Nome</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Tipo</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Estabelecimento</th>
                      <th className="text-right p-4 text-slate-400 font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`user-row-${u.id}`}>
                        <td className="p-4 text-white">{u.name}</td>
                        <td className="p-4 text-slate-300">{u.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded ${
                            u.role === 'super_admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {u.role === 'super_admin' ? 'Super Admin' : 'Admin Loja'}
                          </span>
                        </td>
                        <td className="p-4 text-slate-300">{getEstablishmentName(u.establishment_id)}</td>
                        <td className="p-4 text-right">
                          {u.role !== 'super_admin' && (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setEditingUser(u)} className="text-slate-400 hover:text-white">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setShowResetPassword(u)} className="text-slate-400 hover:text-white">
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(u.id)} className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'notifications' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                Solicitações de Recuperação de Senha
                {pendingCount > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-400">
                    ({pendingCount} pendentes)
                  </span>
                )}
              </h2>
            </div>
            
            {notifications.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-8 text-center">
                  <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Nenhuma solicitação de recuperação de senha.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {notifications.map(notif => (
                  <Card 
                    key={notif.id} 
                    className={`bg-slate-800 border-slate-700 ${notif.status === 'pending' ? 'border-l-4 border-l-amber-500' : ''}`}
                    data-testid={`notification-${notif.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-full ${notif.status === 'pending' ? 'bg-amber-500/20' : 'bg-green-500/20'}`}>
                            {notif.status === 'pending' ? (
                              <Clock className="w-5 h-5 text-amber-400" />
                            ) : (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-white flex items-center gap-2">
                              <Mail className="w-4 h-4 text-slate-400" />
                              Recuperação de Senha
                            </h3>
                            <p className="text-sm text-slate-300 mt-1">
                              <span className="font-medium">{notif.user_name}</span> ({notif.user_email})
                            </p>
                            {notif.message && (
                              <p className="text-sm text-slate-400 mt-2 bg-slate-700/50 p-2 rounded">
                                "{notif.message}"
                              </p>
                            )}
                            <p className="text-xs text-slate-500 mt-2">
                              {new Date(notif.created_at).toLocaleString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {notif.status === 'pending' ? (
                            <>
                              <Button 
                                size="sm" 
                                onClick={() => handleResetPasswordFromNotification(notif.id, notif.user_id, notif.user_email, notif.user_name)}
                                className="bg-red-500 hover:bg-red-600"
                                data-testid={`reset-password-${notif.id}`}
                              >
                                <Key className="w-4 h-4 mr-1" />
                                Resetar Senha
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Resolvido
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'backup' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Backup do Sistema</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stats Card */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <HardDrive className="w-5 h-5 text-blue-400" />
                    Estatísticas do Banco de Dados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {backupStats ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-700/50 p-3 rounded-lg">
                          <p className="text-xs text-slate-400">Usuários</p>
                          <p className="text-2xl font-bold text-white">{backupStats.stats?.users || 0}</p>
                        </div>
                        <div className="bg-slate-700/50 p-3 rounded-lg">
                          <p className="text-xs text-slate-400">Estabelecimentos</p>
                          <p className="text-2xl font-bold text-white">{backupStats.stats?.establishments || 0}</p>
                        </div>
                        <div className="bg-slate-700/50 p-3 rounded-lg">
                          <p className="text-xs text-slate-400">Produtos</p>
                          <p className="text-2xl font-bold text-white">{backupStats.stats?.products || 0}</p>
                        </div>
                        <div className="bg-slate-700/50 p-3 rounded-lg">
                          <p className="text-xs text-slate-400">Categorias</p>
                          <p className="text-2xl font-bold text-white">{backupStats.stats?.categories || 0}</p>
                        </div>
                        <div className="bg-slate-700/50 p-3 rounded-lg col-span-2">
                          <p className="text-xs text-slate-400">Pedidos</p>
                          <p className="text-2xl font-bold text-white">{backupStats.stats?.orders || 0}</p>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-slate-700">
                        <p className="text-sm text-slate-400">
                          Total de documentos: <span className="text-white font-semibold">{backupStats.total_documents}</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-400">Carregando estatísticas...</p>
                  )}
                </CardContent>
              </Card>

              {/* Backup Actions Card */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Download className="w-5 h-5 text-green-400" />
                    Fazer Backup
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <FileJson className="w-6 h-6 text-green-400 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-white">Backup Completo (JSON)</h4>
                          <p className="text-sm text-slate-400 mt-1">
                            Exporta todos os dados do sistema em formato JSON. 
                            Inclui usuários, estabelecimentos, produtos, categorias e pedidos.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleDownloadBackup}
                      disabled={isBackingUp}
                      className="w-full bg-green-500 hover:bg-green-600"
                      data-testid="download-backup-btn"
                    >
                      {isBackingUp ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Criando backup...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Baixar Backup Completo
                        </>
                      )}
                    </Button>

                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
                      <p className="text-sm text-amber-200">
                        <strong>💡 Dica:</strong> Faça backups regularmente e guarde-os em local seguro. 
                        Recomendamos fazer backup antes de qualquer alteração importante no sistema.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Interest Requests Tab */}
        {activeTab === 'interest' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                Solicitações de Interesse
                {pendingInterestCount > 0 && (
                  <span className="ml-2 text-sm font-normal text-slate-400">
                    ({pendingInterestCount} pendentes)
                  </span>
                )}
              </h2>
            </div>
            
            {interestRequests.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Nenhuma solicitação de interesse.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {interestRequests.map(request => (
                  <Card 
                    key={request.id} 
                    className={`bg-slate-800 border-slate-700 ${
                      request.status === 'pending' ? 'border-l-4 border-l-orange-500' : 
                      request.status === 'approved' ? 'border-l-4 border-l-green-500' : 
                      'border-l-4 border-l-red-500'
                    }`}
                    data-testid={`interest-${request.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-white text-lg">{request.store_name}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              request.status === 'pending' ? 'bg-orange-500/20 text-orange-400' : 
                              request.status === 'approved' ? 'bg-green-500/20 text-green-400' : 
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {request.status === 'pending' ? 'Pendente' : 
                               request.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                            <div>
                              <p className="text-slate-500 text-xs">Tipo</p>
                              <p className="text-slate-300">{getStoreTypeLabel(request.store_type, request.store_type_other)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Responsável</p>
                              <p className="text-slate-300">{request.owner_name}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Email</p>
                              <p className="text-slate-300">{request.email}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs">Telefone</p>
                              <p className="text-slate-300">{request.phone}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {request.address ? `${request.address}, ` : ''}{request.city}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(request.created_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                          
                          {request.message && (
                            <div className="mt-3 bg-slate-700/50 p-2 rounded text-sm text-slate-300">
                              <strong>Mensagem:</strong> {request.message}
                            </div>
                          )}
                          
                          {request.status === 'rejected' && request.rejection_reason && (
                            <div className="mt-3 bg-red-500/10 border border-red-500/30 p-2 rounded text-sm text-red-300">
                              <strong>Motivo da rejeição:</strong> {request.rejection_reason}
                            </div>
                          )}
                        </div>
                        
                        {request.status === 'pending' && (
                          <div className="flex gap-2 ml-4">
                            <Button 
                              size="sm"
                              onClick={() => {
                                setShowApproveDialog(request);
                                setApproveSlug(request.store_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
                              }}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`approve-${request.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Aprovar
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => setShowRejectDialog(request)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`reject-${request.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* New Establishment Dialog */}
      <Dialog open={showNewEstablishment} onOpenChange={setShowNewEstablishment}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="new-establishment-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Estabelecimento</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEstablishment} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome</Label>
              <Input
                value={newEstablishment.name}
                onChange={(e) => setNewEstablishment(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                required
                data-testid="est-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Slug (URL)</Label>
              <Input
                value={newEstablishment.slug}
                onChange={(e) => setNewEstablishment(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s/g, '-') }))}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="nome-da-loja"
                required
                data-testid="est-slug-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Telefone</Label>
              <Input
                value={newEstablishment.phone}
                onChange={(e) => setNewEstablishment(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="est-phone-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">WhatsApp</Label>
              <Input
                value={newEstablishment.whatsapp}
                onChange={(e) => setNewEstablishment(prev => ({ ...prev, whatsapp: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="5511999999999"
                data-testid="est-whatsapp-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Descrição</Label>
              <Textarea
                value={newEstablishment.description}
                onChange={(e) => setNewEstablishment(prev => ({ ...prev, description: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                data-testid="est-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewEstablishment(false)}>Cancelar</Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-establishment-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New User Dialog */}
      <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="new-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Usuário</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label className="text-slate-300">Nome</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                required
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                required
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Senha</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
                required
                minLength={6}
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">Estabelecimento</Label>
              <Select value={newUser.establishment_id} onValueChange={(value) => setNewUser(prev => ({ ...prev, establishment_id: value }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="user-establishment-select">
                  <SelectValue placeholder="Selecione um estabelecimento" />
                </SelectTrigger>
                <SelectContent>
                  {establishments.map(est => (
                    <SelectItem key={est.id} value={est.id}>{est.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewUser(false)}>Cancelar</Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-user-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Usuário</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label className="text-slate-300">Nome</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300">Estabelecimento</Label>
                <Select value={editingUser.establishment_id || ''} onValueChange={(value) => setEditingUser(prev => ({ ...prev, establishment_id: value }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Selecione um estabelecimento" />
                  </SelectTrigger>
                  <SelectContent>
                    {establishments.map(est => (
                      <SelectItem key={est.id} value={est.id}>{est.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Cancelar</Button>
                <Button type="submit" className="bg-red-500 hover:bg-red-600">Salvar</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPassword} onOpenChange={() => setShowResetPassword(null)}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Resetar Senha</DialogTitle>
            <DialogDescription className="text-slate-400">
              Uma nova senha temporária será gerada para o usuário {showResetPassword?.name}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPassword(null)}>Cancelar</Button>
            <Button onClick={handleResetPassword} className="bg-red-500 hover:bg-red-600" data-testid="confirm-reset-password">
              Resetar Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Establishment Stats Dialog */}
      <Dialog open={!!showEstablishmentStats} onOpenChange={() => setShowEstablishmentStats(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Store className="w-5 h-5" />
              {showEstablishmentStats?.name}
            </DialogTitle>
          </DialogHeader>
          {establishmentStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="w-5 h-5 text-blue-400" />
                  <span className="text-slate-400 text-sm">Total Pedidos</span>
                </div>
                <p className="text-2xl font-bold text-white">{establishmentStats.total_orders}</p>
              </div>
              <div className="bg-slate-700 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  <span className="text-slate-400 text-sm">Hoje</span>
                </div>
                <p className="text-2xl font-bold text-white">{formatCurrency(establishmentStats.today_revenue)}</p>
              </div>
              <div className="bg-slate-700 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-5 h-5 text-purple-400" />
                  <span className="text-slate-400 text-sm">Produtos</span>
                </div>
                <p className="text-2xl font-bold text-white">{establishmentStats.total_products}</p>
              </div>
              <div className="bg-slate-700 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <ShoppingCart className="w-5 h-5 text-red-400" />
                  <span className="text-slate-400 text-sm">Rejeitados</span>
                </div>
                <p className="text-2xl font-bold text-white">{establishmentStats.rejected_orders}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Password Modal */}
      <Dialog open={!!showNewPasswordModal} onOpenChange={() => setShowNewPasswordModal(null)}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="new-password-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Senha Resetada com Sucesso!
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              A nova senha temporária foi gerada para o usuário.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-700/50 p-4 rounded-lg">
              <p className="text-sm text-slate-400 mb-1">Usuário</p>
              <p className="text-white font-medium">{showNewPasswordModal?.userName}</p>
              <p className="text-slate-300 text-sm">{showNewPasswordModal?.userEmail}</p>
            </div>
            
            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg">
              <p className="text-sm text-green-400 mb-2 font-medium">Nova Senha Temporária</p>
              <div className="flex items-center gap-2">
                <code className="bg-slate-900 px-4 py-2 rounded text-lg font-mono text-green-400 flex-1 text-center" data-testid="temp-password">
                  {showNewPasswordModal?.password}
                </code>
                <Button 
                  size="sm" 
                  onClick={handleCopyPassword}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="copy-password-btn"
                >
                  Copiar
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg">
              <p className="text-sm text-amber-400">
                <strong>Importante:</strong> Envie esta senha para o usuário por um canal seguro (WhatsApp, SMS, etc). O usuário deverá trocar a senha após o primeiro acesso.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowNewPasswordModal(null)} 
              className="bg-red-500 hover:bg-red-600 w-full"
              data-testid="close-password-modal"
            >
              Entendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Interest Dialog */}
      <Dialog open={!!showApproveDialog && !approvalResult} onOpenChange={() => { setShowApproveDialog(null); setApproveSlug(''); }}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Aprovar Solicitação
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Aprovar a solicitação de <strong className="text-white">{showApproveDialog?.store_name}</strong>. 
              Um novo estabelecimento e usuário admin serão criados.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-700/50 p-4 rounded-lg">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500">Responsável</p>
                  <p className="text-white">{showApproveDialog?.owner_name}</p>
                </div>
                <div>
                  <p className="text-slate-500">Email</p>
                  <p className="text-white">{showApproveDialog?.email}</p>
                </div>
                <div>
                  <p className="text-slate-500">Telefone</p>
                  <p className="text-white">{showApproveDialog?.phone}</p>
                </div>
                <div>
                  <p className="text-slate-500">Cidade</p>
                  <p className="text-white">{showApproveDialog?.city}</p>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Slug da Loja (URL) *</Label>
              <Input
                value={approveSlug}
                onChange={(e) => setApproveSlug(e.target.value.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, ''))}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="nome-da-loja"
                data-testid="approve-slug-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                A loja ficará acessível em: /loja/{approveSlug || 'slug'}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApproveDialog(null); setApproveSlug(''); }}>Cancelar</Button>
            <Button onClick={handleApproveInterest} className="bg-green-600 hover:bg-green-700" data-testid="confirm-approve-btn">
              <Check className="w-4 h-4 mr-2" />
              Aprovar e Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Result Dialog */}
      <Dialog open={!!approvalResult} onOpenChange={() => { setApprovalResult(null); setShowApproveDialog(null); setApproveSlug(''); }}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="approval-result-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Estabelecimento Criado!
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-green-500/10 border border-green-500/30 p-4 rounded-lg">
              <p className="text-green-400 font-medium mb-2">Credenciais de Acesso</p>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="text-white font-mono">{approvalResult?.admin_email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Senha Temporária</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-slate-900 px-3 py-1 rounded text-green-400 font-mono">
                      {approvalResult?.temp_password}
                    </code>
                    <Button 
                      size="sm" 
                      onClick={() => {
                        navigator.clipboard.writeText(approvalResult?.temp_password);
                        toast.success('Senha copiada!');
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500">URL da Loja</p>
                  <p className="text-white font-mono">{approvalResult?.store_url}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg">
              <p className="text-sm text-amber-400">
                <strong>Importante:</strong> Envie essas credenciais ao cliente por WhatsApp ou email. 
                Ele deverá trocar a senha após o primeiro acesso.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => { setApprovalResult(null); setShowApproveDialog(null); setApproveSlug(''); }} 
              className="bg-green-600 hover:bg-green-700 w-full"
            >
              Entendi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Interest Dialog */}
      <Dialog open={!!showRejectDialog} onOpenChange={() => { setShowRejectDialog(null); setRejectReason(''); }}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <X className="w-5 h-5 text-red-500" />
              Rejeitar Solicitação
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Rejeitar a solicitação de <strong className="text-white">{showRejectDialog?.store_name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-slate-300">Motivo da Rejeição *</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white mt-2"
              placeholder="Informe o motivo da rejeição..."
              rows={3}
              data-testid="reject-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(null); setRejectReason(''); }}>Cancelar</Button>
            <Button onClick={handleRejectInterest} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-btn">
              <X className="w-4 h-4 mr-2" />
              Rejeitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block/Unblock Establishment Dialog */}
      <Dialog open={!!showBlockDialog} onOpenChange={() => { setShowBlockDialog(null); setBlockReason(''); }}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="block-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {showBlockDialog?.is_blocked ? (
                <>
                  <Check className="w-5 h-5 text-green-500" />
                  Desbloquear Estabelecimento
                </>
              ) : (
                <>
                  <Ban className="w-5 h-5 text-red-500" />
                  Bloquear Estabelecimento
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {showBlockDialog?.is_blocked 
                ? `Desbloquear "${showBlockDialog?.name}" e permitir que volte a operar.`
                : `Bloquear "${showBlockDialog?.name}" impedirá novos pedidos e mostrará aviso de pagamento pendente.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {!showBlockDialog?.is_blocked && (
            <div className="py-4">
              <Label className="text-slate-300">Motivo do Bloqueio</Label>
              <Textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white mt-2"
                placeholder="Ex: Mensalidade em atraso desde 01/03/2026"
                rows={2}
                data-testid="block-reason-input"
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBlockDialog(null); setBlockReason(''); }}>Cancelar</Button>
            {showBlockDialog?.is_blocked ? (
              <Button onClick={() => handleBlockEstablishment(false)} className="bg-green-600 hover:bg-green-700" data-testid="confirm-unblock-btn">
                <Check className="w-4 h-4 mr-2" />
                Desbloquear
              </Button>
            ) : (
              <Button onClick={() => handleBlockEstablishment(true)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-block-btn">
                <Ban className="w-4 h-4 mr-2" />
                Bloquear
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
